import { RadioButtonChecked, RadioButtonUnchecked } from '@mui/icons-material';
import ArrowRightAltRoundedIcon from '@mui/icons-material/ArrowRightAltRounded';
import CircleIcon from '@mui/icons-material/Circle';
import RocketLaunchIcon from '@mui/icons-material/RocketLaunch';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import { Button, Collapse, Stack, Typography } from '@mui/material';
import { ProductGroups } from 'enums';
import getLocalizedText from 'features/co-pilot/translationFileProduct';
import React from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { makeStyles } from 'tss-react/mui';

interface ItemProps {
  isActive: boolean;
  isViewed: boolean;
  nextActive?: boolean;
  isFirst?: boolean;
  isLast?: boolean;
  title: string;
  primaryText: string;
  secondaryText: string;
}

const StageItem: React.FC<ItemProps> = ({ isActive, isViewed, nextActive, isFirst, isLast, title, primaryText, secondaryText }) => {
  const { classes, cx } = useStyles();

  return (
    <Stack direction={'row'} gap={2} sx={{ width: '100%' }}>
      <Stack alignItems={'center'}>
        <div className={cx(classes.topConnector, isFirst && 'first', isViewed && 'active')} />
        {isActive ? (
          <RadioButtonChecked className={cx(classes.radioIcon, 'active')} />
        ) : isViewed ? (
          <CircleIcon className={classes.dotIcon} />
        ) : (
          <RadioButtonUnchecked className={classes.radioIcon} />
        )}
        {!isLast && <div className={cx(classes.connector, nextActive && 'active')} />}
      </Stack>
      <div className={cx(classes.stageItemContainer, isLast && 'last')}>
        <div className={classes.stageItemTitleContainer}>
          <Typography className={cx(classes.stageItemTitle, isActive && 'active', isViewed && 'viewed')}>{title}</Typography>
        </div>
        <Collapse in={isViewed} unmountOnExit={true}>
          <Stack direction={'row'} gap={4}>
            <Typography className={classes.stageItemText}>{primaryText}</Typography>
            <Typography className={classes.stageItemText}>{secondaryText}</Typography>
          </Stack>
        </Collapse>
      </div>
    </Stack>
  );
};

interface Props {
  stage: 1 | 2 | 3 | 4 | 5;
  productGroup: ProductGroups;

  handleClose?(): void;
}

const CoPilotExplanation: React.FC<Props> = ({ stage, productGroup, handleClose }) => {
  const { classes, cx } = useStyles();
  const { t } = useTranslation();

  const renderStage = () => {
    switch (stage) {
      case 1:
        return <img className={classes.image} src={'/dashboard/images/onboarding/onboarding_co_pilot_1.jpg'} alt={'stage-1'} />;
      case 2:
        return <img className={classes.image} src={'/dashboard/images/onboarding/onboarding_co_pilot_2.jpg'} alt={'stage-2'} />;
      case 3:
        return <img className={classes.image} src={'/dashboard/images/onboarding/onboarding_co_pilot_3.jpg'} alt={'stage-3'} />;
      case 4:
        return <img className={classes.image} src={'/dashboard/images/onboarding/onboarding_co_pilot_4.jpg'} alt={'stage-4'} />;
      case 5:
        return (
          <Stack className={classes.actionContainer} gap={3}>
            <Typography className={classes.actionTitle}>{getLocalizedText('superchargeOutreach', productGroup, t)}</Typography>
            <Typography className={classes.actionSubtitle}>{getLocalizedText('aiHandlesDetails', productGroup, t)}</Typography>
            <Stack direction={'row'}>
              <Button
                className={classes.actionButton}
                variant={'text'}
                color={'primary'}
                startIcon={<ArrowRightAltRoundedIcon />}
                disableRipple={true}
                onClick={handleClose}
              >
                {t('createCampaignHere')}
              </Button>
            </Stack>
          </Stack>
        );
      default:
        return null;
    }
  };
  return (
    <Stack direction={'row'} alignItems={'center'} sx={{ height: '100%' }}>
      <div className={classes.leftContent}>
        <div className={classes.header}>
          <Typography className={classes.headerTitle} fontWeight={'bold'}>
            {getLocalizedText('automateOutreach', productGroup, t)}
          </Typography>
          <Typography className={classes.headerSubtitle}>{t('aiOutreachTool')}</Typography>
        </div>
        <Stack className={classes.body} gap={2}>
          <Stack direction={'row'} spacing={4} sx={{ paddingLeft: '40px', marginBottom: '24px' }}>
            <Stack className={classes.colHead} direction={'row'} alignItems={'center'} justifyContent={'center'} gap={2}>
              <RocketLaunchIcon className={classes.icon} />
              <Typography className={classes.colHeadText}>
                <Trans i18nKey="youThePilotStrong">
                  <strong>You</strong> - The Pilot
                </Trans>
              </Typography>
            </Stack>
            <Stack className={classes.colHead} direction={'row'} alignItems={'center'} justifyContent={'center'} gap={2}>
              <SmartToyIcon className={classes.icon} />
              <Typography className={classes.colHeadText}>
                <Trans i18nKey="aiTheCoPilotStrong">
                  <strong>The AI</strong> - The Co-Pilot
                </Trans>
              </Typography>
            </Stack>
          </Stack>
          <div>
            <StageItem
              isActive={stage === 1}
              isViewed={stage >= 1}
              isFirst={true}
              nextActive={stage >= 2}
              title={getLocalizedText('stage1Title', productGroup, t)}
              primaryText={getLocalizedText('stage1Primary', productGroup, t)}
              secondaryText={getLocalizedText('stage1Secondary', productGroup, t)}
            />
            <StageItem
              isActive={stage === 2}
              isViewed={stage >= 2}
              nextActive={stage >= 3}
              title={getLocalizedText('stage2Title', productGroup, t)}
              primaryText={getLocalizedText('stage2Primary', productGroup, t)}
              secondaryText={getLocalizedText('stage2Secondary', productGroup, t)}
            />
            <StageItem
              isActive={stage === 3}
              isViewed={stage >= 3}
              nextActive={stage >= 4}
              title={getLocalizedText('stage3Title', productGroup, t)}
              primaryText={getLocalizedText('stage3Primary', productGroup, t)}
              secondaryText={getLocalizedText('stage3Secondary', productGroup, t)}
            />
            <StageItem
              isActive={stage >= 4}
              isViewed={stage >= 4}
              isLast={true}
              title={getLocalizedText('stage4Title', productGroup, t)}
              primaryText={getLocalizedText('stage4Primary', productGroup, t)}
              secondaryText={getLocalizedText('stage4Secondary', productGroup, t)}
            />
          </div>
        </Stack>
      </div>
      <div className={cx(classes.rightContent, stage === 5 && 'action')}>{renderStage()}</div>
    </Stack>
  );
};

const useStyles = makeStyles()((theme) => ({
  leftContent: {
    flexBasis: '55%',
    height: '100%',
    overflowY: 'auto',
  },
  header: {
    padding: '16px 32px',
    borderBottom: `1px solid ${theme.palette.greys.light}`,
  },
  headerTitle: {
    fontSize: theme.typography.pxToRem(25),
    fontWeight: 'bold',
  },
  headerSubtitle: {
    fontSize: theme.typography.pxToRem(20),
    color: theme.palette.text.secondary,
  },
  body: {
    padding: '24px 64px',
  },
  colHead: {
    padding: '8px 24px',
    background: 'linear-gradient(to right, #5044FF, #4AA4FF)',
    borderRadius: '10px',
    color: '#FFF',
    flex: 1,
  },
  colHeadText: {
    fontSize: theme.typography.pxToRem(18),
  },
  icon: {
    fontSize: theme.typography.pxToRem(24),
  },
  radioIcon: {
    fontSize: theme.typography.pxToRem(24),
    color: theme.palette.greys.light,
    '&.active': {
      color: theme.palette.primary.main,
    },
  },
  dotIcon: {
    fontSize: theme.typography.pxToRem(12),
    margin: '6px',
    color: theme.palette.primary.main,
  },
  topConnector: {
    height: '16px',
    width: '2px',
    background: theme.palette.greys.light,
    marginBottom: '-2px',
    '&.first': {
      width: '0px',
      marginBottom: '0px',
    },
    '&.active': {
      background: theme.palette.primary.main,
    },
  },
  connector: {
    height: 'calc(100% + 16px + 9.5px)',
    width: '2px',
    background: theme.palette.greys.light,
    marginTop: '-2px',
    '&.active': {
      background: theme.palette.primary.main,
    },
  },
  stageItemContainer: {
    width: '100%',
    paddingBottom: '16px',
    '&.last': {
      paddingBottom: '0px',
    },
  },
  stageItemTitleContainer: {
    background: theme.palette.backgrounds.main_new,
    padding: '8px 16px',
    borderRadius: '10px',
    width: '100%',
  },
  stageItemTitle: {
    fontSize: theme.typography.pxToRem(18),
    color: theme.palette.text.secondary,
    '&.viewed': {
      color: theme.palette.text.primary,
    },
    '&.active': {
      color: theme.palette.primary.main,
      fontWeight: 'bold',
    },
  },
  stageItemText: {
    fontSize: theme.typography.pxToRem(15),
    color: theme.palette.text.secondary,
    padding: '8px 16px',
    flex: 1,
  },
  rightContent: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    flexBasis: '45%',
    backgroundColor: theme.palette.backgrounds.main_new,
    borderRadius: '16px',
    height: '100%',
    padding: '0 0 80px 0',
    '&.action': {
      padding: '80px 0',
    },
  },
  image: {
    width: 'auto',
    height: 'auto',
    maxWidth: '100%',
    maxHeight: '100%',
    objectFit: 'cover',
  },
  actionContainer: {
    padding: '32px 100px',
    display: 'flex',
    flexDirection: 'column',
  },
  actionButton: {
    fontSize: theme.typography.pxToRem(30),
    fontWeight: 'normal',
    lineHeight: 1.2,
    color: theme.palette.primary.main,
    textTransform: 'none',
    padding: '0px',
    '&:hover': {
      textDecoration: 'underline',
      textUnderlineOffset: '8px',
      background: 'unset',
    },
    '& .MuiButton-startIcon': {
      '& > *:first-of-type': {
        fontSize: theme.typography.pxToRem(40),
      },
    },
  },
  actionTitle: {
    fontSize: theme.typography.pxToRem(40),
    fontWeight: 'bold',
    lineHeight: 1.2,
  },
  actionSubtitle: {
    fontSize: theme.typography.pxToRem(30),
    color: theme.palette.text.secondary,
    lineHeight: 1.2,
  },
}));

export default CoPilotExplanation;
